import { Head, Link, router } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MasterclassCard } from "@/components/masterclasses/masterclass-card"
import { MasterclassEmptyState } from "@/components/masterclasses/masterclass-empty-state"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import { masterclassesPath, newMasterclassPath } from "@/routes"
import type {
  MasterclassTab,
  MasterclassesIndexProps,
} from "@/types/masterclasses"

const TABS: readonly MasterclassTab[] = ["all", "published", "draft"] as const

/**
 * OKS-54 — `Brand/Masterclasses/Index`.
 *
 * Header (H1 + Create CTA), Tabs (All / Published / Draft with counts),
 * card grid (3-col desktop, 2-col tablet, 1-col mobile per UX Spec §5).
 *
 * Tab filter is server-side (TRD KD-9). Click → partial reload
 * `only: ['masterclasses', 'current_tab', 'state_counts']` so the
 * header + Create CTA stay rendered while only the grid refreshes.
 *
 * 5-state matrix (UX Spec §3 + ux-states skill):
 *   - **Empty** (zero masterclasses ever) → `<MasterclassEmptyState>` warm panel.
 *   - **Loading** — Inertia visits resolve before render; partial-reload
 *     latency is sub-render in practice. Vitest covers the skeleton fallback.
 *   - **Error** — covered by the global Inertia error boundary + the
 *     standard 404/500 layouts.
 *   - **Success** → grid renders.
 *   - **Edge — tab returns zero** (e.g. Draft tab w/ 0 drafts) → inline
 *     `EmptyTabPanel` inside the tab content (no CTA — header CTA still
 *     reachable).
 */
export default function MasterclassesIndex({
  masterclasses,
  current_tab,
  state_counts,
  is_empty,
  can_create,
}: MasterclassesIndexProps) {
  const { t } = useTranslation()

  const onTabChange = (next: string) => {
    if (!TABS.includes(next as MasterclassTab)) return
    router.get(masterclassesPath(), next === "all" ? {} : { tab: next }, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
      only: ["masterclasses", "current_tab", "state_counts"],
    })
  }

  return (
    <AppLayout>
      <Head title={t("masterclasses.head_title")} />
      <div
        className="flex flex-col gap-6 p-6"
        data-testid="masterclasses-index-page"
      >
        <header className="flex items-center justify-between gap-3">
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("masterclasses.index.title")}
          </h1>
          {can_create && (
            <Button asChild data-testid="masterclasses-new-cta">
              <Link href={newMasterclassPath()}>
                <Plus className="mr-1 size-4" aria-hidden="true" />
                {t("masterclasses.index.new_cta")}
              </Link>
            </Button>
          )}
        </header>

        {is_empty ? (
          <MasterclassEmptyState />
        ) : (
          <>
            <Tabs
              value={current_tab}
              onValueChange={onTabChange}
              data-testid="masterclasses-tabs"
            >
              <TabsList>
                {TABS.map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    data-testid={`masterclasses-tab-${tab}`}
                  >
                    {t(`masterclasses.tabs.${tab}`)}
                    <span
                      className="text-muted-foreground ml-1.5 tabular-nums"
                      data-testid={`masterclasses-tab-${tab}-count`}
                    >
                      {state_counts[tab]}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {masterclasses.length === 0 ? (
              <EmptyTabPanel tab={current_tab} />
            ) : (
              <div
                className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
                data-testid="masterclasses-grid"
              >
                {masterclasses.map((m) => (
                  <MasterclassCard key={m.id ?? 0} masterclass={m} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

/**
 * Inline empty panel for the "tab returns zero" edge state. Distinct
 * from the page-level `<MasterclassEmptyState>` which fires only when
 * `is_empty` (brand has zero rows ever). Per UX Spec §3 Edge: no CTA
 * here — the header "Create masterclass" is still reachable.
 */
function EmptyTabPanel({ tab }: { tab: MasterclassTab }) {
  const { t } = useTranslation()

  // Only published/draft have specific "nothing here" copy. The "all"
  // tab can't render with zero rows AND non-zero state_counts at the
  // same time — if all tabs are zero, the page falls through to the
  // page-level `is_empty` branch above.
  const key = tab === "all" ? "draft" : tab

  return (
    <Card
      className="flex flex-col items-center justify-center py-10 text-center"
      data-testid={`masterclasses-tab-empty-${tab}`}
    >
      <p className="text-muted-foreground text-sm">
        {t(`masterclasses.tabs.empty_in_tab.${key}`)}
      </p>
    </Card>
  )
}
